<template>
  <nav class="panel filtros-licitacoes" data-tour="licitacoes-filtros">
    <div class="panel-heading is-flex is-align-items-center is-justify-content-space-between">
      <span>Filtros</span>
      <slot name="heading-extra" />
    </div>
    <div class="panel-block is-block">
      <FiltrosLicitacoesPregoeiro
        v-if="persona === 'pregoeiro'"
        :value="draft"
        :situacoes="situacoes"
        :modalidades="modalidades"
        :tipos-edital="tiposEdital"
        :tipos-pregao="tiposPregao"
        :purchasing-units="purchasingUnits"
        :purchasing-units-loading="purchasingUnitsLoading"
        @input="updateDraft"
        @commit="flushAutoFilter"
        @open="suspendAuto"
        @close="resumeAuto"
      />
      <FiltrosLicitacoesFornecedor
        v-else
        :value="draft"
        :situacoes="situacoes"
        :ufs="ufs"
        :modalidades="modalidades"
        :tipos-edital="tiposEdital"
        :tipos-pregao="tiposPregao"
        @input="updateDraft"
        @commit="flushAutoFilter"
        @open="suspendAuto"
        @close="resumeAuto"
      />
      <div class="filtros-licitacoes__actions">
        <FiltroRapidoChips
          data-tour="licitacoes-chips"
          :persona="persona"
          :value="activeBucket"
          :disabled="loading"
          @input="changeBucket"
        />
        <div class="buttons">
          <button
            type="button"
            class="button is-outlined"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
            data-tour="licitacoes-limpar"
            @click="clearFilters"
          >
            Limpar
          </button>
          <button
            type="button"
            class="button is-primary"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
            data-tour="licitacoes-filtrar"
            @click="submitFilter"
          >
            Filtrar
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import debounce from 'lodash/debounce';
import {
  getSituacaoIdsForBucket,
  MODALIDADES,
  SITUACOES,
  situacaoIdsMatchBucket,
  TIPOS_EDITAL,
  TIPOS_PREGAO,
  UFS,
} from '@/utils/licitacoesBuckets';
import { areLicitacoesFilterCriteriaEqual } from '@/utils/licitacoesFiltersQuery';
import FiltroRapidoChips from './FiltroRapidoChips';
import FiltrosLicitacoesFornecedor from './FiltrosLicitacoesFornecedor';
import FiltrosLicitacoesPregoeiro from './FiltrosLicitacoesPregoeiro';

const AUTO_FILTER_DEBOUNCE_MS = 900;

export default {
  name: 'FiltrosLicitacoesPanel',
  components: {
    FiltroRapidoChips,
    FiltrosLicitacoesFornecedor,
    FiltrosLicitacoesPregoeiro,
  },
  props: {
    persona: { type: String, default: 'visitante' },
    value: { type: Object, required: true },
    /** Filtros da última pesquisa aplicada (URL / store). */
    applied: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    purchasingUnits: { type: Array, default: () => [] },
    purchasingUnitsLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      draft: this.copy(this.value),
      suspendAutoFilter: false,
      syncingFromSelf: false,
    };
  },
  computed: {
    isStale() {
      if (!this.applied) return false;
      return !areLicitacoesFilterCriteriaEqual(this.draft, this.applied);
    },
    // Recalculado sempre a partir da seleção real: draft.bucket sozinho não sobrevive
    // a um round-trip pela URL (undefined e null viram a mesma coisa lá), então não dá
    // pra confiar nele puro para decidir qual chip mostrar como ativo.
    activeBucket() {
      return situacaoIdsMatchBucket(this.draft.situacaoIds, this.draft.bucket)
        ? this.draft.bucket
        : undefined;
    },
    situacoes() {
      // Catálogo completo no multiselect; o chip só pré-seleciona (não reduz a lista).
      if (this.persona === 'pregoeiro') return SITUACOES;
      return SITUACOES.filter(situacao => situacao.id !== 1);
    },
    modalidades() {
      return MODALIDADES;
    },
    tiposEdital() {
      return TIPOS_EDITAL;
    },
    tiposPregao() {
      return TIPOS_PREGAO;
    },
    ufs() {
      return UFS;
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        // Evita cancelar o debounce ao ecoar o próprio v-model (ex.: digitação em Objeto).
        if (this.syncingFromSelf) return;
        this.draft = this.copy(value);
        this.cancelAutoFilter();
      },
    },
  },
  created() {
    this.debouncedAutoFilter = debounce(() => {
      this.emitFilterIfStale();
    }, AUTO_FILTER_DEBOUNCE_MS);
  },
  beforeDestroy() {
    this.cancelAutoFilter();
  },
  methods: {
    copy(value) {
      return {
        ...value,
        abertura: { ...(value.abertura || {}) },
        publicacao: { ...(value.publicacao || {}) },
      };
    },
    updateDraft(filters, { auto = true } = {}) {
      let next = this.copy(filters);
      // Se a Situação deixa de bater com o chip (inclusive "Todas", que só bate com seleção vazia),
      // nenhum chip fica selecionado (bucket !== null !== "Todas").
      if (!situacaoIdsMatchBucket(next.situacaoIds, next.bucket)) {
        next = { ...next, bucket: undefined };
      }
      this.draft = next;
      this.syncingFromSelf = true;
      this.$emit('input', this.draft);
      this.$nextTick(() => {
        this.syncingFromSelf = false;
      });
      if (auto && !this.suspendAutoFilter) this.scheduleAutoFilter();
    },
    scheduleAutoFilter() {
      this.debouncedAutoFilter();
    },
    cancelAutoFilter() {
      if (this.debouncedAutoFilter && this.debouncedAutoFilter.cancel) {
        this.debouncedAutoFilter.cancel();
      }
    },
    suspendAuto() {
      this.suspendAutoFilter = true;
      this.cancelAutoFilter();
    },
    resumeAuto() {
      this.suspendAutoFilter = false;
    },
    flushAutoFilter() {
      this.suspendAutoFilter = false;
      this.cancelAutoFilter();
      this.emitFilterIfStale();
    },
    submitFilter() {
      this.suspendAutoFilter = false;
      this.cancelAutoFilter();
      // Clique explícito: refaz a busca mesmo sem alterações nos critérios.
      this.$emit('filter', this.copy(this.draft));
    },
    emitFilterIfStale() {
      if (!this.isStale) return;
      this.$emit('filter', this.copy(this.draft));
    },
    changeBucket(bucket) {
      const filters = {
        ...this.draft,
        bucket,
        // Chip pré-seleciona as situações do bucket; "Todas" limpa a seleção.
        situacaoIds: getSituacaoIdsForBucket(bucket),
        pagina: 1,
      };
      this.updateDraft(filters, { auto: false });
      this.cancelAutoFilter();
      this.$emit('chip', filters);
    },
    clearFilters() {
      this.cancelAutoFilter();
      this.$emit('clear');
    },
  },
};
</script>

<style lang="scss" scoped>
.filtros-licitacoes > .panel-block {
  padding: 0.75em;
}

// Espaçamento alinhado ao legado (campo a ~12px do vizinho; label mais colada ao controle).
.filtros-licitacoes ::v-deep .filtros-licitacoes-row {
  margin-left: -0.375rem;
  margin-right: -0.375rem;
  margin-top: -0.375rem;

  &:last-child {
    margin-bottom: -0.375rem;
  }

  > .column {
    padding: 0.375rem;
  }

  .label:not(:last-child) {
    margin-bottom: 0.25em;
  }

  .field:not(:last-child) {
    margin-bottom: 0;
  }
}

.filtros-licitacoes__actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
}

.filtros-licitacoes__actions .buttons {
  margin-bottom: 0;
}

// Faixa intermediária (pregoeiro e licitante/visitante): 3 campos por linha.
// Especificidade acima de `.column.is-*-desktop` do Bulma.
@media screen and (min-width: 1024px) and (max-width: 1365px) {
  .filtros-licitacoes ::v-deep .filtros-licitacoes-row > .column.filtros-licitacoes__campo {
    flex: none;
    width: 33.3333%;
  }

  .filtros-licitacoes ::v-deep .filtros-licitacoes-row > .column.filtros-licitacoes__campo--objeto {
    width: 100%;
  }
}

@media (max-width: 1023px) {
  .filtros-licitacoes__actions {
    align-items: stretch;
    flex-direction: column;
    gap: 0.75rem;
  }

  .filtros-licitacoes__actions .buttons {
    justify-content: flex-end;
    margin-bottom: 0;
  }
}
</style>
